/**
 * ::use widgets:
 * - PrettyUseWidget: L0 pretty widget for ::use[id]{from="..."} block
 * - UseButtonGroupWidget: Run/Stop button for ::use header
 */

import { WidgetType } from "@codemirror/view";
import { t as translate } from "@magic-md/i18n";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    createClearExecutionButton,
    createInlineRenderToggleButton,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTemplateInputsButton,
    createTextSpan,
    createWatchIndicator,
    shieldGroupEvents,
} from "../shared/widgets";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

type UseSourceKind = "definition" | "content" | "for" | "use";

function sourceKindBadge(kind: UseSourceKind): string {
    if (kind === "content") return "C";
    if (kind === "for") return "F";
    if (kind === "use") return "U";
    return "D";
}

/* ── PrettyUseWidget — L0 pretty widget for ::use header ── */

export class PrettyUseWidget extends WidgetType {
    constructor(
        readonly useId: string,
        readonly source: string,
        readonly summary: string,
        readonly sourceKind: UseSourceKind = "definition",
        readonly isImported: boolean = false,
        readonly pluginName: string | null = null,
        readonly isTemplate: boolean = false,
        readonly isWatched: boolean = false,
    ) {
        super();
    }
    eq(other: PrettyUseWidget) {
        return (
            other.useId === this.useId &&
            other.source === this.source &&
            other.summary === this.summary &&
            other.sourceKind === this.sourceKind &&
            other.isImported === this.isImported &&
            other.pluginName === this.pluginName &&
            other.isTemplate === this.isTemplate &&
            other.isWatched === this.isWatched
        );
    }
    toDOM(): HTMLElement {
        const icon = createSyntaxBadge("U", {
            sub: sourceKindBadge(this.sourceKind),
            sup: this.isImported ? "M" : undefined,
            template: this.isTemplate,
        });
        icon.classList.add("cm-magic-use-pretty-icon");

        const source = createTextSpan("cm-magic-use-pretty-source", this.source);
        source.dataset.magicNavTarget = this.source;
        source.dataset.magicNavKind = "use-source";

        const primary = [
            createTextSpan("cm-magic-use-pretty-id", this.useId),
            createTextSpan("cm-magic-use-pretty-arrow", "←"),
            source,
        ];
        if (this.sourceKind === "definition" && this.pluginName) {
            primary.push(
                createTextSpan("cm-magic-use-pretty-plugin", this.pluginName),
            );
        }
        if (this.isTemplate) {
            primary.push(createTextSpan("cm-magic-use-pretty-template", "template"));
        }
        if (this.isWatched) {
            primary.push(createWatchIndicator(this.useId));
        }

        return createPrettySyntaxShell({
            wrapperClass: "cm-magic-use-pretty",
            badge: icon,
            primary,
            detail: null,
        });
    }
    ignoreEvent(): boolean {
        return false;
    }
}

/* ── UseButtonGroupWidget — Run/Stop + Pin + Config buttons for ::use block header ── */

export class UseButtonGroupWidget extends WidgetType {
    constructor(
        readonly useId: string,
        readonly status: ExecutionStatus,
        /** If the use block references a plugin, include its name for config/pin support */
        readonly pluginName: string | null = null,
        readonly sourceKind: UseSourceKind = "definition",
        readonly canRun: boolean = true,
        readonly canInlineRender: boolean = true,
        readonly inlineRendered: boolean = false,
        readonly isTemplate: boolean = false,
    ) {
        super();
    }
    eq(other: UseButtonGroupWidget) {
        return (
            other.useId === this.useId &&
            other.status === this.status &&
            other.pluginName === this.pluginName &&
            other.sourceKind === this.sourceKind &&
            other.canRun === this.canRun &&
            other.canInlineRender === this.canInlineRender &&
            other.inlineRendered === this.inlineRendered &&
            other.isTemplate === this.isTemplate
        );
    }
    toDOM(): HTMLElement {
        const group = document.createElement("span");
        group.className = "cm-magic-btn-group";

        // Prevent mouse events from reaching CodeMirror
        shieldGroupEvents(group);

        const isRunning = this.status === "running";
        const useId = this.useId;
        const sourceLabel = this.sourceKind === "content"
            ? tr("syntax.widget.use.contentInstance")
            : tr("syntax.widget.use.useInstance");

        // Run/Stop button
        const runBtn = document.createElement("span");
        runBtn.className = isRunning
            ? "cm-magic-btn cm-magic-btn-stop"
            : "cm-magic-btn cm-magic-btn-run";
        runBtn.textContent = isRunning ? "■" : "▶";
        runBtn.title = isRunning
            ? tr("syntax.widget.use.stop", { sourceLabel, id: this.useId })
            : tr("syntax.widget.use.run", { sourceLabel, id: this.useId });
        runBtn.setAttribute(
            "aria-label",
            runBtn.title,
        );
        runBtn.addEventListener("mousedown", (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isRunning) {
                window.dispatchEvent(
                    new CustomEvent("magic-md:cancel-use", {
                        detail: { useId },
                    }),
                );
            } else {
                window.dispatchEvent(
                    new CustomEvent("magic-md:run-use", {
                        detail: { useId },
                    }),
                );
            }
        });

        if (this.canRun && !this.isTemplate) {
            group.appendChild(runBtn);
        }

        // Config button — opens the definition popover for all ::use blocks
        // (plugin-sourced uses have ConfigPanel; content-sourced uses have bind/with editors)
        {
            const cfgBtn = document.createElement("span");
            cfgBtn.className = "cm-magic-btn cm-magic-btn-config";
            cfgBtn.textContent = "⚙";
            cfgBtn.title = this.pluginName
                ? tr("syntax.widget.use.configurePlugin", { pluginName: this.pluginName, id: this.useId })
                : tr("syntax.widget.use.configureSource", { sourceLabel, id: this.useId });
            cfgBtn.setAttribute("aria-label", cfgBtn.title);
            cfgBtn.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();
                const rect = cfgBtn.getBoundingClientRect();
                const detail: {
                    definitionId: string;
                    x: number;
                    y: number;
                    sourceElement?: HTMLElement;
                    unitKind?: "use";
                } = {
                    definitionId: useId,
                    x: rect.left,
                    y: rect.bottom,
                };
                Object.defineProperty(detail, "sourceElement", {
                    value: cfgBtn,
                    enumerable: false,
                });
                Object.defineProperty(detail, "unitKind", {
                    value: "use",
                    enumerable: false,
                });
                window.dispatchEvent(
                    new CustomEvent("magic-md:select-definition", {
                        detail,
                    }),
                );
            });
            group.appendChild(cfgBtn);
        }

        if (this.isTemplate) {
            group.appendChild(
                createTemplateInputsButton(
                    "use",
                    useId,
                    tr("syntax.widget.use.editInputs", { id: this.useId }),
                ),
            );
        }

        // Inline render toggle
        if (this.canInlineRender && !this.isTemplate) {
            group.appendChild(
                createInlineRenderToggleButton(
                    useId,
                    this.inlineRendered,
                    this.inlineRendered
                        ? tr("syntax.widget.use.hideInlineRender", { id: this.useId })
                        : tr("syntax.widget.use.showInlineRender", { id: this.useId }),
                ),
            );
        }

        // Pin button — add/remove use block in the workspace sidebar (only for plugin-type sources)
        if (this.pluginName) {
            const pinBtn = document.createElement("span");
            pinBtn.className = "cm-magic-btn cm-magic-btn-pin";
            pinBtn.textContent = "⊙";
            pinBtn.title = tr("syntax.widget.use.pin", { pluginName: this.pluginName, id: this.useId });
            pinBtn.setAttribute("aria-label", pinBtn.title);
            pinBtn.addEventListener("mousedown", (e) => {
                e.preventDefault();
                e.stopPropagation();
                window.dispatchEvent(
                    new CustomEvent("magic-md:pin-definition", {
                        detail: { definitionId: useId },
                    }),
                );
            });
            group.appendChild(pinBtn);
        }

        if (this.status === "error") {
            group.appendChild(
                createClearExecutionButton(
                    useId,
                    tr("syntax.widget.use.clearError", { id: this.useId }),
                    tr("syntax.widget.use.clearError", { id: this.useId }),
                ),
            );
        }

        return group;
    }
    ignoreEvent(): boolean {
        return true;
    }
}
